import React from 'react';
import { cn } from '../../../utils/cn';

interface MessageContentProps {
  content: string;
}

export function MessageContent({ content }: MessageContentProps) {
  // Process the content to handle markdown-like formatting and links
  const processContent = (text: string) => {
    // Remove any bullet points at the beginning of lines
    let processed = text.replace(/^•\s*/gm, '');
    
    // Handle bold text (replace **text** with bold styling)
    processed = processed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    
    // Handle markdown style links first
    processed = processed.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g, 
      (match, linkText, url) => {
        // Clean the URL if it contains JavaScript code
        if (url.includes("', '_blank')")) {
          url = url.split("', '_blank'")[0];
        }
        
        // Check if it's an email link
        if (url.startsWith('mailto:') || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(url)) {
          const emailAddress = url.startsWith('mailto:') ? url.substring(7) : url;
          return `<a href="mailto:${emailAddress}" class="text-brand-600 hover:underline font-medium" target="_blank">${linkText}</a>`;
        }
        
        // Check if it's a file download
        const isFileDownload = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|txt|csv)$/i.test(url);
        if (isFileDownload) {
          return `<a href="${url}" download class="text-brand-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer">Download ${linkText}</a>`;
        }
        
        // Regular link
        return `<a href="${url}" class="text-brand-600 hover:underline" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      }
    );
    
    // Handle plain URLs with a more robust regex
    const urlRegex = /(?<![="\w])(https?:\/\/[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b[-a-zA-Z0-9()@:%_+.~#?&/=]*)(?!["\w])/g;
    processed = processed.replace(
      urlRegex,
      (url) => {
        // Clean the URL if it contains JavaScript code
        if (url.includes("', '_blank')")) {
          url = url.split("', '_blank'")[0];
        }
        
        const isFileDownload = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|txt|csv)$/i.test(url);
        if (isFileDownload) {
          return `<a href="${url}" class="text-brand-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer">Download</a>`;
        }
        return `<a href="${url}" class="text-brand-600 hover:underline" target="_blank" rel="noopener noreferrer">${url}</a>`;
      }
    );
    
    // Handle plain email addresses
    const emailRegex = /(?<![^\s<>])([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?![^\s<>])/g;
    processed = processed.replace(
      emailRegex,
      (email) => {
        return `<a href="mailto:${email}" class="text-brand-600 hover:underline" target="_blank">${email}</a>`;
      }
    );
    
    return processed;
  };

  // Handle address blocks with line breaks
  const processAddressBlock = (text: string) => {
    const addressKeywords = ['Industrieweg', 'Assendelft', 'Nederland', 'Telefoonnummer', 'E-mail', 'Website'];
    const isAddressBlock = addressKeywords.some(keyword => text.includes(keyword));
    
    if (isAddressBlock) {
      return text.split('\n').map((line, i) => {
        // Special handling for lines with "Website:" or "E-mail:" labels
        if (line.includes('Website:')) {
          const parts = line.split('Website:');
          const label = parts[0] + 'Website:';
          let url = parts[1].trim();
          
          // Clean the URL if it contains JavaScript code
          if (url.includes("', '_blank')")) {
            url = url.split("', '_blank'")[0];
          }
          
          return (
            <React.Fragment key={i}>
              <span>{label} </span>
              <a 
                href={url.startsWith('http') ? url : `https://${url}`} 
                className="text-brand-600 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {url}
              </a>
              {i < text.split('\n').length - 1 && <br />}
            </React.Fragment>
          );
        } else if (line.includes('E-mail:')) {
          const parts = line.split('E-mail:');
          const label = parts[0] + 'E-mail:';
          const email = parts[1].trim();
          return (
            <React.Fragment key={i}>
              <span>{label} </span>
              <a 
                href={`mailto:${email}`}
                className="text-brand-600 hover:underline"
                target="_blank"
              >
                {email}
              </a>
              {i < text.split('\n').length - 1 && <br />}
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={i}>
              <span dangerouslySetInnerHTML={{ __html: processContent(line) }} />
              {i < text.split('\n').length - 1 && <br />}
            </React.Fragment>
          );
        }
      });
    }
    
    return <span dangerouslySetInnerHTML={{ __html: processContent(text) }} />;
  };

  // Clean leading bullets, dots, or whitespace
  const cleanContent = (text: string) => {
    return text.replace(/^\s*[•\.\-]\s*/gm, '');
  };

  const paragraphs = content.split('\n\n');
  
  return (
    <div className="whitespace-pre-wrap space-y-4 text-black">
      {paragraphs.map((paragraph, index) => {
        const cleanedParagraph = cleanContent(paragraph);
        
        // Handle lists
        if (cleanedParagraph.includes('-')) {
          const lines = cleanedParagraph.split('\n');
          const title = lines[0].includes('-') ? '' : lines.shift();
          const items = lines.filter(line => line.trim().length > 0);
          
          return (
            <div key={index} className="space-y-2">
              {title && (
                <div className="font-medium text-black mb-2">
                  {processAddressBlock(title)}
                </div>
              )}
              <ul className="list-none space-y-2">
                {items.map((item, itemIndex) => (
                  <li 
                    key={`${index}-${itemIndex}`}
                    className="pl-4 relative text-black flex items-start gap-2"
                  >
                    <span className="text-brand-500 mt-1">•</span>
                    <span className="flex-1">{processAddressBlock(item.replace(/^-/, '').trim())}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        
        // Handle questions with emphasis
        if (cleanedParagraph.includes('?')) {
          return (
            <div key={index} className={cn(
              "font-medium text-black",
              cleanedParagraph.length < 50 ? "italic" : ""
            )}>
              {processAddressBlock(cleanedParagraph)}
            </div>
          );
        }
        
        // Regular paragraphs
        return (
          <div 
            key={index} 
            className="text-black leading-relaxed"
          >
            {processAddressBlock(cleanedParagraph)}
          </div>
        );
      })}
    </div>
  );
}